import { useState } from "react";
import {
  AlertCircle, ExternalLink, Loader2, File as FileIcon, FileImage, FileSpreadsheet, FileText, Presentation, Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useDeleteNoteFile, useNoteFiles, type NoteUploads } from "@/hooks/useNoteFiles";
import type { NoteFile } from "@/services/plannerNotesService";

/**
 * The Notes card's email-style Attachments section: the files in the client's Drive
 * folder, uploads in progress (each with its own bar), and a link to the folder.
 * Files open in Drive in a new tab; Remove moves a file to the Drive trash.
 */

/** "812 B", "340 KB", "2.5 MB". */
function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function TypeIcon({ file }: { file: NoteFile }) {
  const mime = file.mime_type ?? "";
  const cls = "h-4 w-4 shrink-0 text-muted-foreground";
  if (mime.startsWith("image/")) return <FileImage className={cls} aria-hidden="true" />;
  if (/spreadsheet|excel|csv/.test(mime)) return <FileSpreadsheet className={cls} aria-hidden="true" />;
  if (/presentation|powerpoint/.test(mime)) return <Presentation className={cls} aria-hidden="true" />;
  if (/pdf|word|text|document/.test(mime)) return <FileText className={cls} aria-hidden="true" />;
  return <FileIcon className={cls} aria-hidden="true" />;
}

export function NoteAttachments({
  tenantId, enabled, uploads,
}: {
  tenantId?: string | null;
  /** The backend has a Drive folder configured; otherwise nothing is shown or fetched. */
  enabled: boolean;
  uploads: NoteUploads;
}) {
  const { data, isLoading, isError, refetch } = useNoteFiles(tenantId, enabled);
  const remove = useDeleteNoteFile(tenantId);
  const [toRemove, setToRemove] = useState<NoteFile | null>(null);
  if (!enabled) return null;

  const files = data?.files ?? [];
  const folderUrl = data?.folder_url;
  const empty = !isLoading && !isError && files.length === 0 && uploads.pending.length === 0;

  return (
    <section role="region" aria-label="Attachments" className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">Attachments</h3>
        {folderUrl ? (
          <a
            href={folderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-link hover:underline"
          >
            Open folder in Drive <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading attachments">
          <div className="h-8 w-full animate-pulse rounded bg-muted" />
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn't load the attachments.{" "}
          <button type="button" className="text-link hover:underline" onClick={() => refetch()}>Try again</button>
        </p>
      ) : null}

      {empty ? <p className="text-sm text-muted-foreground">No attachments yet</p> : null}

      {uploads.pending.length || files.length ? (
        <ul className="divide-y divide-border rounded-md border border-border">
          {uploads.pending.map((p) => (
            <li key={p.key} className="flex items-center gap-3 px-3 py-2 text-sm">
              {p.status === "error" ? (
                <>
                  <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-foreground" title={p.name}>{p.name}</span>
                  <span className="text-xs text-destructive">{p.error}</span>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => uploads.dismiss(p.key)}>
                    Dismiss
                  </Button>
                </>
              ) : (
                <>
                  <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-foreground" title={p.name}>{p.name}</span>
                  <div
                    role="progressbar"
                    aria-label={`Uploading ${p.name}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={p.percent}
                    className="h-1.5 w-24 overflow-hidden rounded-full bg-muted"
                  >
                    <div className="h-full bg-primary transition-all" style={{ width: `${p.percent}%` }} />
                  </div>
                  <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">{p.percent}%</span>
                </>
              )}
            </li>
          ))}
          {files.map((f) => {
            const removing = remove.isPending && remove.variables?.id === f.id;
            return (
            <li
              key={f.id}
              className={`flex items-center gap-3 px-3 py-2 text-sm transition-opacity motion-reduce:transition-none ${removing ? "opacity-50" : ""}`}
            >
              <TypeIcon file={f} />
              <a
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                title={f.name}
                className="min-w-0 flex-1 truncate text-link hover:underline"
              >
                {f.name}
              </a>
              {removing ? (
                <span role="status" className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Removing…
                </span>
              ) : (
                <>
                  <span className="text-xs tabular-nums text-muted-foreground">{formatFileSize(f.size)}</span>
                  <span className="hidden text-xs text-muted-foreground sm:inline">{formatDate(f.created_time)}</span>
                </>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                aria-label={`Remove ${f.name}`}
                title="Remove"
                disabled={remove.isPending}
                onClick={() => setToRemove(f)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
            );
          })}
        </ul>
      ) : null}

      <AlertDialog open={toRemove !== null} onOpenChange={(open) => { if (!open && !remove.isPending) setToRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this file?</AlertDialogTitle>
            <AlertDialogDescription>
              {toRemove?.name} moves to the Google Drive trash, where it can be restored for 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending} onClick={() => setToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              aria-busy={remove.isPending || undefined}
              onClick={() => {
                // The dialog stays open (spinner) until the request settles, then closes.
                if (toRemove) remove.mutate(toRemove, { onSettled: () => setToRemove(null) });
              }}
            >
              {remove.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Removing…
                </span>
              ) : (
                "Remove"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default NoteAttachments;
